import React, { useEffect } from "react";
import { ArrowRight, ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";
import LazyLoad from 'react-lazyload';
import AOS from 'aos';
import 'aos/dist/aos.css'; // Import AOS styles

const faqs = [
  {
    question: "I work in a team, can we all use the same account?",
    answer:
      "Yes, we understand that the easiness of the platform can help multiple team members to benefit from it including an event planner, a secretary, marketing managers, sales employees, or anyone at your organization. You can share the account credentials with your team, but for enhanced security and personalized access, consider upgrading to a plan that supports multiple user roles or sub-accounts.",
  },
  {
    question: "My QR code doesn’t work, what should I do?",
    answer:
      "If your QR code isn’t working, first ensure it’s being scanned with a compatible QR code reader. Check that the QR code is not damaged or distorted. Verify that the linked URL or action is active and correctly configured in your account dashboard. If the issue persists, contact our support team at support@example.com or through the help center for assistance.",
  },
  {
    question: "How many events and QR codes can I create?",
    answer:
      "The number of events and QR codes you can create depends on your subscription plan. Free accounts typically allow up to 3 events and 5 QR codes, while premium plans offer unlimited events and QR codes. Check your account dashboard or visit our pricing page at https://example.com/pricing for detailed limits.",
  },
  {
    question: "Where can I find my event details and responses?",
    answer:
      "You can find your event details and responses in your account dashboard. Navigate to the 'Events' section, select the specific event, and view details like attendee responses, analytics, and more. You can also export this data for further analysis.",
  },
  {
    question: "Can I create multiple QR codes for different events?",
    answer:
      "Yes, you can create multiple QR codes for different events. Each event in your dashboard allows you to generate a unique QR code tailored to specific actions, such as registration, check-in, or feedback collection. Simply go to the 'QR Codes' section in your event settings to create and manage them.",
  },
  {
    question: "How can I download my generated leads data?",
    answer:
      "To download your leads data, go to the 'Leads' or 'Analytics' section in your account dashboard. Select the event or campaign, then click the 'Export' button to download the data as a CSV or Excel file. You can customize the data fields before exporting to suit your needs.",
  },
  {
    question: "How can I collect payments?",
    answer:
      "You can collect payments by integrating a payment gateway (e.g., Stripe, PayPal) with your event. In the event setup, go to the 'Payments' section, connect your preferred payment provider, and configure ticket prices or donation options. Attendees can then pay via the QR code or event link, and funds will be deposited into your linked account.",
  },
];

export default function HowItWork() {
  const [activeIndex, setActiveIndex] = useState(0);

  // Initialize AOS
  useEffect(() => {
    AOS.init({
      duration: 800, // Animation duration in milliseconds
      once: true, // Animations occur only once
      offset: 100, // Trigger animations 100px before element enters viewport
    });
  }, []);

  const toggle = (index: number) => {
    setActiveIndex(index === activeIndex ? -1 : index);
  };

  return (
    <>
      {/* Hero Section */}
      <section className="flex flex-col" data-aos="fade-up">
        {/* Main Content */}
        <div className="flex-grow">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between px-6 py-20 gap-10">
            {/* Text Section */}
            <div className="max-w-xl space-y-5" data-aos="fade-right" data-aos-delay="100">
              <h2 className="text-5xl font-bold text-[#234042] Bebas-neue">HOW IT WORKS</h2>
              <div className="flex items-center space-x-2 text-lg Bebas-neue">
                <span className="text-[#234042]">HOME</span>
                <span className="h-4 w-[1px] bg-gray-400"></span>
                <span className="text-[#F47321] tracking-light">HOW IT WORKS</span>
              </div>
              <p className="text-[#234042] text-base leading-relaxed helvetica-neue">
                Discover how easy it is to create, customize, and manage your QR codes with LiveQR.
                In just a few simple steps, you’ll be sharing smart, trackable links that boost engagement and deliver results.
              </p>
            </div>

            {/* Image */}
            <div className="relative" data-aos="zoom-in" data-aos-delay="200">
              <LazyLoad height={400} offset={50} once>
                <img
                  src="/images/hiw.png"
                  alt="LiveQR process illustration"
                  className="w-full max-w-md rounded-lg"
                />
              </LazyLoad>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="bg-[#156E77]" data-aos="fade-up" data-aos-delay="300">
          <p className="max-w-7xl mx-auto py-4 px-6 md:px-10 text-white !text-sm text-left lg:!text-xl helvetica-neue">
            Reach prospects by using your own QR code for data collection
          </p>
        </div>
      </section>

      {/* Section 2: Registration */}
      <section className="bg-[#234042] py-12 px-4 md:px-12" data-aos="fade-up" data-aos-delay="100">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div className="bg-white rounded-xl p-6 flex justify-center" data-aos="zoom-in" data-aos-delay="200">
            <LazyLoad height={400} offset={50} once>
              <img
                src="/images/step1.svg"
                alt="User registering for LiveQR"
                className="w-full max-w-md h-auto p-[50px]"
              />
            </LazyLoad>
          </div>
          <div className="text-white text-left max-w-xl helvetica-neue" data-aos="fade-left" data-aos-delay="300">
            <h3 className="text-[#F47321] font-bold text-3xl mb-2 Bebas-neue">STEP 01</h3>
            <h2 className="text-3xl lg:text-5xl font-bold uppercase mb-4 leading-tight Bebas-neue">
              Registration
            </h2>
            <p className="mb-4 ml-7 text-white/70">
              Create an account with LiveQR by filling out your details:
            </p>
            <ul className="list-inside text-base sm:text-lg space-y-1 mb-4 ml-14 text-white/70">
              <li>- Name</li>
              <li>- Email</li>
              <li>- Create Password</li>
            </ul>
            <p className="ml-7 text-white/70">
              By signing up, you agree to our Privacy Policy and Terms of Use and can begin using the platform.
            </p>
          </div>
        </div>
      </section>

      {/* Section 3: Event & QR Code */}
      <section className="bg-white py-12 px-4 lg:px-16" data-aos="fade-up" data-aos-delay="100">
        <div className="max-w-7xl mx-auto flex flex-col-reverse lg:grid lg:grid-cols-2 gap-10 items-center">
          {/* Text Section */}
          <div className="text-left max-w-xl" data-aos="fade-right" data-aos-delay="200">
            <h3 className="text-[#F47321] font-bold text-3xl mb-2 Bebas-neue">STEP 02</h3>
            <h2 className="Bebas-neue text-3xl lg:text-5xl text-[#234042] font-bold uppercase mb-4 leading-tight Bebas-neue">
              Create an Event and Generate the QR Code
            </h2>
            <p className="text-base lg:!text-2xl mb-4 text-[#234042] ml-7 helvetica-neue">
              Create an event:
            </p>
            <ul className="list-inside text-base sm:text-lg text-[#234042] space-y-2 mb-4 ml-14 helvetica-neue">
              <li>- Fill in event details: Name, Venue, Start & End Date</li>
              <li>- Upload a picture of your event</li>
              <li>
                - Select the information to collect from visitors: Full Name, Email, Mobile Number, Address
              </li>
              <li>- Drag and drop question types, then customize accordingly</li>
            </ul>
          </div>

          {/* Image Section */}
          <div className="flex justify-center" data-aos="zoom-in" data-aos-delay="300">
            <LazyLoad height={400} offset={50} once>
              <img
                src="/images/step2.svg"
                alt="QR code for event managers"
                className="w-full max-w-md h-auto py-[60px]"
              />
            </LazyLoad>
          </div>
        </div>
      </section>

      {/* Section 4 */}
      <section className="bg-[#234042] py-12 px-4 md:px-12" data-aos="fade-up" data-aos-delay="100">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div className="bg-white rounded-xl p-6 flex justify-center" data-aos="zoom-in" data-aos-delay="200">
            <LazyLoad height={400} offset={50} once>
              <img
                src="/images/step3.svg"
                alt="User registering for LiveQR"
                className="w-full max-w-md h-auto p-[50px]"
              />
            </LazyLoad>
          </div>
          <div className="text-white text-left max-w-xl" data-aos="fade-left" data-aos-delay="300">
            <h3 className="text-[#F47321] font-bold text-3xl mb-2 Bebas-neue">STEP 03</h3>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold uppercase mb-4 leading-tight Bebas-neue">
              Download and share your QR code or copy URL
            </h2>
            <p className="mb-4 text-white/70 ml-7 helvetica-neue">
              Once you’ve filled in the needed details, you can click “Generate QR Code”.
            </p>
            <ul className="list-inside text-base sm:text-lg space-y-1 mb-4 text-white/70 ml-14 helvetica-neue">
              <li>- Download your QR code as a high quality PNG file</li>
              <li>- Test your QR code by scanning it through your mobile phone</li>
              <li>- Post the QR code where it can be seen by your target audience</li>
              <li>- Ask your visitors/customers/attendees to scan your QR code</li>
            </ul>
            <p className="text-white/70 ml-7 helvetica-neue">
              Easy, right? The information you collect via your QR code will be stored and collected in your LiveQR account and you can access it anytime.
            </p>
          </div>
        </div>
      </section>

      {/* Section 5 */}
      <div
        className="bg-cover bg-center text-white py-20 bg-[#156E77]"
        style={{ backgroundImage: "url('/images/Clip.png')" }}
        data-aos="fade-up" data-aos-delay="100"
      >
        <div className="max-w-7xl mx-auto lg:px-4 px-6">
          <div className="flex flex-col lg:flex-row lg:items-end items-start  justify-between gap-6">
            {/* Heading */}
            <h1 className="text-3xl lg:text-5xl font-bold uppercase text-left tracking-wide flex-1 Bebas-neue" data-aos="fade-right" data-aos-delay="200">
              Innovation lead generation for sales managers and marketers. Try it yourself now!
            </h1>

            {/* Button */}
            <button
              type="button"
              className="bg-white uppercase btn-hover-white text-black rounded-none whitespace-nowrap transition transform scale-y-125 text-xs lg:text-[18px] lg:px-4 lg:py-3 px-2 py-2 flex items-center"
              data-aos="fade-left" data-aos-delay="300"
            >
              <span>CREATE YOUR QR CODE</span>
              <span className="icon-arrow-right">
                <ArrowRight className="w-4 h-4 ml-2" />
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Section 6: FAQ */}
      <section className="max-w-7xl mx-auto lg:px-6 px-10 py-20 text-[#234042]" data-aos="fade-up" data-aos-delay="100">
        <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row items-start md:items-center mb-8 space-y-4 md:space-y-0 md:space-x-20">
          <h2 className="text-2xl md:text-5xl font-bold text-[#234042] whitespace-nowrap uppercase Bebas-neue" data-aos="fade-right" data-aos-delay="200">
            FAQ
          </h2>
          <p className="text-[#234042] text-left text-md flex-1 helvetica-neue" data-aos="fade-left" data-aos-delay="300">
            LiveQR is a modern data collection tool that helps events organizers, marketers, sales people to generate QR code for each event. It allows visitors to scan the QR code and fill in their information.
          </p>
        </div>

        {/* FAQs */}
        <div className="space-y-4">
          {faqs.map((faq, index) => {
            const isOpen = activeIndex === index;
            return (
              // <LazyLoad key={index} height={200} offset={50} once>
                <div data-aos="fade-up" data-aos-delay={`${index * 100}`}>
                  <div
                    className="flex items-start justify-between cursor-pointer border-b border-gray-200 py-3"
                    onClick={() => toggle(index)}
                  >
                    <div className="flex items-start gap-5">
                      <span className="text-[#F47321] font-light lg:font-normal text-[25px] lg:text-[30px]">Q</span>
                      <h3 className="font-light lg:font-normal uppercase text-[25px] lg:text-[30px] max-w-3xl Bebas-neue">
                        {faq.question}
                      </h3>
                    </div>
                    <button
                      type="button"
                      className={`w-8 h-8 flex items-center justify-center transition-all rounded-none text-white ${
                        isOpen ? "bg-[#F47321]" : "bg-[#234042]"
                      } hover:bg-opacity-90`}
                    >
                      {isOpen ? (
                        <ChevronUp className="w-4 h-4 text-white" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-white" />
                      )}
                    </button>
                  </div>
                  {isOpen && faq.answer && (
                    <p className="ml-7 mt-2 text-gray-600 w-full helvetica-neue lg:mb-10">
                      {faq.answer}
                    </p>
                  )}
                </div>
              // </LazyLoad>
            );
          })}
        </div>
      </section>
    </>
  );
}